import { useReducedMotion } from "framer-motion";
import { useA11yPrefs } from "@/state/useA11yPrefs";

/**
 * True when motion should be suppressed — from EITHER the OS
 * `prefers-reduced-motion` setting or the site's own "Reduce motion" toggle.
 *
 * Why this exists rather than calling framer's `useReducedMotion()` directly:
 * that hook reads ONLY the OS media query. It does not consult
 * `MotionConfigContext`, so `A11yProvider`'s `<MotionConfig reducedMotion="always">`
 * has no effect on it (the hook that does read MotionConfig is framer's internal
 * `useReducedMotionConfig`, which only `motion` components use). MotionConfig
 * still governs `motion` component animations, so components whose reduced-motion
 * behaviour is expressed *through* `motion` props are partly covered — but any
 * component that gates plain JS on the boolean (a rAF loop, an early return, a
 * `setTimeout`) must use this hook or the in-app toggle silently does nothing.
 *
 * Verified against framer-motion 12.38.0.
 */
export function useReducedMotionPref(): boolean {
  const osPrefers = useReducedMotion();
  const inAppToggle = useA11yPrefs((s) => s.reduceMotion);
  return Boolean(osPrefers) || inAppToggle;
}
